import React, { useEffect, useRef } from "react";
import { FaTimes } from "react-icons/fa";

export default function DemoModal({ isOpen, onClose, formData, handleChange, handleSubmit, status }) {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Focus the close button when modal opens
      if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      }

      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          onClose();
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "";
      };
    }
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-modal-title"
    >
      <div className="bg-white p-6 md:p-8 rounded-xl shadow-2xl w-full max-w-xl relative max-h-[90vh] overflow-y-auto">
        <button
          ref={closeButtonRef}
          onClick={onClose}
          className="absolute top-4 right-4 text-snaptrix-gray-500 hover:text-snaptrix-gray-700 text-2xl p-2 rounded-full hover:bg-snaptrix-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-snaptrix-blue-600"
          aria-label="Close modal"
        >
          <FaTimes aria-hidden="true" />
        </button>

        <h2 id="signup-modal-title" className="text-2xl md:text-3xl font-bold mb-6 text-center text-snaptrix-gray-700">
          Sign Up
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-snaptrix-gray-600 mb-1">
              Name
            </label>
            <input
              ref={firstInputRef}
              type="text"
              id="name"
              name="name"
              placeholder="Your full name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-3 border border-snaptrix-gray-300 rounded-lg focus:ring-2 focus:ring-snaptrix-blue-600 focus:border-snaptrix-blue-600 outline-none transition"
              required
              aria-required="true"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-snaptrix-gray-600 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="you@example.com"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 border border-snaptrix-gray-300 rounded-lg focus:ring-2 focus:ring-snaptrix-blue-600 focus:border-snaptrix-blue-600 outline-none transition"
              required
              aria-required="true"
            />
          </div>

          <div>
            <label htmlFor="clubName" className="block text-sm font-medium text-snaptrix-gray-600 mb-1">
              Club Name
            </label>
            <input
              type="text"
              id="clubName"
              name="clubName"
              placeholder="Your cricket club name"
              value={formData.clubName}
              onChange={handleChange}
              className="w-full p-3 border border-snaptrix-gray-300 rounded-lg focus:ring-2 focus:ring-snaptrix-blue-600 focus:border-snaptrix-blue-600 outline-none transition"
              required
              aria-required="true"
            />
          </div>

          <div>
            <label htmlFor="playCricketUrl" className="block text-sm font-medium text-snaptrix-gray-600 mb-1">
              Play Cricket URL
            </label>
            <input
              type="url"
              id="playCricketUrl"
              name="playCricketUrl"
              placeholder="https://play-cricket.com/..."
              value={formData.playCricketUrl}
              onChange={handleChange}
              className="w-full p-3 border border-snaptrix-gray-300 rounded-lg focus:ring-2 focus:ring-snaptrix-blue-600 focus:border-snaptrix-blue-600 outline-none transition"
              required
              aria-required="true"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-snaptrix-blue-600 text-white py-3 px-6 rounded-lg hover:bg-snaptrix-blue-700 transition-colors font-semibold focus:outline-none focus:ring-4 focus:ring-snaptrix-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={status === "Sending..."}
          >
            {status === "Sending..." ? "Sending..." : "Sign Up"}
          </button>
        </form>

        {status && (
          <p
            className={`text-center mt-4 ${
              status.includes("account will be created")
                ? "text-green-600 font-semibold"
                : status === "Sending..."
                ? "text-snaptrix-blue-600"
                : "text-red-600"
            }`}
            role="status"
            aria-live="polite"
          >
            {status}
          </p>
        )}
      </div>
    </div>
  );
}
